<template>
  <CustomDashboard
    :user-id="userId"
    :dashboard="dashboard"
    :dashboard-owners="dashboardOwners"
  />
</template>

<script>
import axios from 'axios'
import CustomDashboard from '@/components/CustomDashboard'
import { getData, createWeatherQuery, createHolidayQuery } from '@/helpers/custom-dashboard'
import moment from 'moment-timezone'

export default {
  components: {
    CustomDashboard
  },

  props: {
    userId: Number
  },

  data () {
    return {
      context: 'dashboards',
      dashboard: DASHBOARD,
      queryLimit: 100000, // Limit for the number of results returned.
      dashboardId: 46,
      dashboardOwners: '',
      initialSummaryDataLoaded: false,
      dashboardOpenLogged: false,
      dashboardLoadStart: 0,
      dashboardLoadEnd: 0
    }
  },

  computed: {
    summaryQuery () {
      let query = `SELECT * FROM (SELECT * FROM ${this.dashboard.dataSource} WHERE `
      this.dashboard.widgets[0].filters.forEach(index => {
        const widget = this.dashboard.widgets[index]
        if (widget.metadata.customField || widget.metadata.type === 'table') {
          return
        }

        if (['checkbox', 'select-multiple'].includes(widget.metadata.type)) {
          query += `${widget.metadata.name} IN ('${widget.value.join('\', \'')}') AND `
        } else if (['select', 'input'].includes(widget.metadata.type)) {
          query += `${widget.metadata.name} = '${widget.value}' AND `
        }
      })
      query += `1=1 ORDER BY ${this.dashboard.widgets[0].headerColumn.name} DESC LIMIT ${this.dashboard.widgets[4].value}) ORDER BY ${this.dashboard.widgets[0].headerColumn.name}`

      return query
    },

    isDefaultFilter () {
      const filters = ['product', 'display_name', 'date_agg', 'number_filter']
      const filterValueMap = {}

      this.dashboard.widgets.forEach(widget => {
        if (widget.metadata.name && filters.includes(widget.metadata.name.toLowerCase())) {
          filterValueMap[widget.metadata.name.toLowerCase()] = widget.value
        }
      })

      return (
        (filterValueMap['product'] && filterValueMap['product'].toLowerCase() === 'all products') &&
        (filterValueMap['display_name'] && filterValueMap['display_name'].toLowerCase() === 'all regions - all regions') &&
        (filterValueMap['date_agg'] && filterValueMap['date_agg'].toLowerCase() === 'weekly') &&
        (filterValueMap['number_filter'] && parseInt(filterValueMap['number_filter']) === 8)
      )
    },

    latestDataDate () {
      return this.dashboard.widgets[0].value.reduce((acc, row) => {
        if (!acc) {
          return row.CALENDAR_DATE
        } else {
          return new Date(row.CALENDAR_DATE) > new Date(acc) ? row.CALENDAR_DATE : acc
        }
      }, '')
    },

    earliestDataDate () {
      return this.dashboard.widgets[0].value.reduce((acc, row) => {
        if (!acc) {
          return row.CALENDAR_DATE
        } else {
          return new Date(row.CALENDAR_DATE) < new Date(acc) ? row.CALENDAR_DATE : acc
        }
      }, '')
    },

    priorDataQuery () {
      const dateUnit = this.dashboard.widgets[3].value.toLowerCase() === 'monthly' ? 'month' : 'week'
      const priorStartDate = this.$moment(this.earliestDataDate).subtract(1, dateUnit).format('YYYY-MM-DD')
      const priorEndDate = this.$moment(this.latestDataDate).subtract(1, dateUnit).format('YYYY-MM-DD')
      let query = `SELECT dateadd(${dateUnit}, 1, CALENDAR_DATE) as NEXT_CALENDAR_DATE,* FROM ${this.dashboard.dataSource} WHERE `

      this.dashboard.widgets[0].filters.forEach(index => {
        const widget = this.dashboard.widgets[index]
        if (widget.metadata.customField || widget.metadata.type === 'table') {
          return
        }

        if (['checkbox', 'select-multiple'].includes(widget.metadata.type)) {
          query += `${widget.metadata.name} IN ('${widget.value.join('\', \'')}') AND `
        } else if (['select', 'input'].includes(widget.metadata.type)) {
          query += `${widget.metadata.name} = '${widget.value}' AND `
        }
      })

      query += `CALENDAR_DATE between date('${priorStartDate}') and date('${priorEndDate}')`

      return query
    }
  },

  watch: {
    summaryQuery () {
      this.loadSummaryData()
    },

    dashboard: {
      deep: true,
      handler (dashboard) {
        const queryParams = dashboard.widgets
          .filter(widget => !['table', 'table-by-value', 'text', 'blank', 'report', 'form-dynamic-text'].includes(widget.metadata.type))
          .reduce((acc, cur) => {
            acc[cur.metadata.name.toLowerCase()] = cur.value ? cur.value.toString() : ''
            return acc
          }, {})
        this.$router.replace({ name: this.$route.name, query: queryParams })
      }
    }
  },

  beforeCreate () {
    sessionStorage.setItem('context', 'dashboards')
  },

  async created () {
    this.dashboardLoadStart = performance.now()
    axios.get(`${process.env.VUE_APP_API_URI}/dashboards/${this.dashboardId}`)
      .then(response => {
        const dashboards = [...response.data]
        if (dashboards.length) {
          const sharedDashboard = { ...dashboards[0] }
          this.dashboardOwners = sharedDashboard.owners
        }
      })

    this.dashboard.widgets
      .filter(widget => ['table', 'table-by-value', 'text', 'blank', 'report', 'form-dynamic-text'].every(value => value.indexOf(widget.metadata.type) < 0) && !widget.metadata.customField)
      .forEach(widget => getData(widget.query, this.queryLimit).then(result => {
        this.$dashboardQueryLog(this.dashboard.title.name, result.data.queryId, widget.query, `${widget.metadata.name}Query`)
        if (['select', 'select-multiple'].includes(widget.metadata.type)) {
          return (widget.options = result.data.data.map(option => ({
            name: option[widget.metadata.name],
            value: option[widget.metadata.name]
          })))
        } else {
          return (widget.options = result.data.data.map(option => option[widget.metadata.name]))
        }
      }))

    // backfilling filters from URL params
    Object.keys(this.$route.query).map(param => {
      this.dashboard.widgets.forEach(filter => {
        if (filter.metadata.name && filter.metadata.name.toLowerCase() === param) {
          filter.value = this.$route.query[param]
        }
      })
    })

    // Make sure checkbox types are array not string
    this.dashboard.widgets.forEach(filter => {
      if (['checkbox', 'select-multiple'].includes(filter.metadata.type) && typeof filter.value === 'string') {
        filter.value = filter.value.split(',')
      }
    })

    await this.loadSummaryData(this.isDefaultFilter)
    this.initialSummaryDataLoaded = true
  },

  updated () {
    if (this.initialSummaryDataLoaded) {
      if (!this.dashboardOpenLogged) {
        this.$nextTick(() => {
          this.dashboardLoadEnd = performance.now()
          this.$customDashboardOpenLog(this.dashboard.title.name, this.dashboardLoadEnd - this.dashboardLoadStart)
          this.dashboardOpenLogged = true
        })
      }
    }
  },

  methods: {
    /**
     * Get valid date for date selection
     * @param {String} type DAY, WEEK or MONTH
     * @param {String} date to validate
     * @return {String} valid date for filter
     */
    getValidDate (type, date) {
      switch (type) {
        case 'DAY':
          return this.$moment(date).diff(this.$moment().subtract(1, 'days'), 'days') <= 0 ? date : this.$moment().subtract(1, 'days')
        case 'WEEK':
          return this.$moment(date).diff(this.$moment().subtract(1, 'weeks'), 'weeks') < 0
            ? this.$moment(date).startOf('isoWeek').format('YYYY-MM-DD')
            : this.$moment().subtract(1, 'weeks').startOf('isoWeek').format('YYYY-MM-DD')
        case 'MONTH':
          return this.$moment(date).diff(this.$moment().subtract(1, 'months'), 'months') < 0
            ? this.$moment(date).startOf('month').format('YYYY-MM-DD')
            : this.$moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD')
        default:
          return date
      }
    },

    async loadSummaryData (useCache = false) {
      try {
        this.dashboard.widgets[0].loading = true
        const result = await (useCache ? this.getCachedMetricSummaryData() : getData(this.summaryQuery, this.queryLimit))
        this.$dashboardQueryLog(this.dashboard.title.name, useCache ? 'cached' : result.data.queryId, useCache ? 'cached' : this.summaryQuery, 'summaryQuery')

        if (result.data.error) {
          throw new Error(result.data.error.message)
        }

        if (result.data.data.length) {
          const startDate = result.data.data[0]['CALENDAR_DATE']
          const endDate = result.data.data[result.data.data.length - 1]['CALENDAR_DATE']
          const regionId = result.data.data[0]['REGION_ID']

          this.setContextData(startDate, endDate, regionId)

          const updatedTime = moment(result.data.data[0]['_ROW_UPDATED_AT'], 'YYYY-MM-DD HH:mm:ss').tz('America/Los_Angeles').format('MMM DD, YYYY hh:mm A z')

          this.dashboard.widgets[6].value = `<b>Last Updated:</b> ${updatedTime}`
        }

        this.dashboard.widgets[0].value = Object.freeze(result.data.data)
        this.setValueChangeMap()
        this.dashboard.widgets[0].loading = false
      } catch (error) {
        console.error(`Error while loading metrics summary data: ${error.message}`)
        this.$Message.error({
          content: 'Error while loading Metrics Summary Dashboard',
          duration: 5
        })
      }
    },

    async setContextData (startDate, endDate, regionId) {
      try {
        this.dashboard.widgets[0].headerColumn.contextTooltipDisplayName = ''
        this.dashboard.widgets[0].headerColumn.contextTooltip = []
        this.dashboard.widgets[0].headerColumn.contextData = {}

        const weatherQuery = createWeatherQuery(startDate, endDate, [regionId], this.dashboard.widgets[3].value)
        const holidayQuery = createHolidayQuery(startDate, endDate, [regionId], this.dashboard.widgets[3].value)
        const [weatherResult, holidayResult] = await Promise.all([
          getData(weatherQuery, this.queryLimit),
          getData(holidayQuery, this.queryLimit)
        ])

        if (this.dashboard.widgets[3].value.toLowerCase() === 'daily') {
          this.dashboard.widgets[0].headerColumn.contextTooltipDisplayName = 'Calendar Date'
          this.dashboard.widgets[0].headerColumn.contextTooltip = Object.freeze(this.createDailyContextTooltip())
          this.dashboard.widgets[0].headerColumn.contextData = Object.freeze(this.createDailyContextData(weatherResult.data.data, holidayResult.data.data))
        } else if (this.dashboard.widgets[3].value.toLowerCase() === 'weekly') {
          this.dashboard.widgets[0].headerColumn.contextTooltipDisplayName = 'Week of'
          this.dashboard.widgets[0].headerColumn.contextTooltip = Object.freeze(this.createWeeklyContextTooltip())
          this.dashboard.widgets[0].headerColumn.contextData = Object.freeze(this.createWeeklyContextData(weatherResult.data.data, holidayResult.data.data))
        }
      } catch (error) {
        console.error(`Error while creating context data: ${error.message}`)
      }
    },

    createWeeklyContextTooltip () {
      return [
        {
          displayName: 'Temperature (\xB0C)',
          key: 'TEMPERATURE_TITLE'
        },
        {
          key: 'WEATHER_CHART',
          chart: {
            dataTraces: [
              {
                x: 'CALENDAR_DATE',
                y: 'MAX_TEMP',
                color: 'red',
                name: 'Max'
              },
              {
                x: 'CALENDAR_DATE',
                y: 'MIN_TEMP',
                color: 'blue',
                name: 'Min'
              }
            ],
            xAxis: {
              tickFormat: '%a',
              tickStep: 1000 * 60 * 60 * 24 // increment by 1 day
            }
          }
        }, {
          displayName: 'Precipitation (mm)',
          key: 'PRECIPITATION_TITLE'
        }, {
          key: 'WEATHER_CHART',
          chart: {
            dataTraces: [
              {
                x: 'CALENDAR_DATE',
                y: 'PRECIPITATION',
                color: 'blue'
              }
            ],
            xAxis: {
              tickFormat: '%a',
              tickStep: 1000 * 60 * 60 * 24
            },
            yAxis: {
              nonNegative: true
            }
          }
        }, {
          displayName: 'Holiday(s)',
          key: 'HOLIDAYS',
          format: 'list-string',
          delimiter: ', ',
          emptyDefault: 'None'
        }
      ]
    },

    createWeeklyContextHolidayArr (holidays) {
      const sortedHolidays = [...holidays].sort((holidayA, holidayB) => new Date(holidayA.date) - new Date(holidayB.date))

      return sortedHolidays.map(holiday => `${holiday.name} (${this.$moment(holiday.date).format('ddd')})`)
    },

    createDateToHolidaysMapWeeklyAgg (holidayData) {
      return holidayData.reduce((acc, holiday) => {
        const weeklyCalendarDate = holiday.CALENDAR_DATE
        const daysToAdd = holiday.DAY_OF_WEEK - 1
        const holidayCalendarDate = this.$moment(weeklyCalendarDate).add(daysToAdd, 'days').format('YYYY-MM-DD')

        if (!acc[weeklyCalendarDate]) {
          acc[weeklyCalendarDate] = []
        }

        acc[weeklyCalendarDate].push({
          date: holidayCalendarDate,
          name: holiday.HOLIDAY
        })

        return acc
      }, {})
    },

    setWeeklyContextTitles (contextData) {
      Object.keys(contextData).forEach(key => {
        const context = contextData[key]
        const weatherChartData = context.WEATHER_CHART
        const avgMaxTemp = weatherChartData.reduce((acc, weatherChartPoint) => acc + weatherChartPoint.MAX_TEMP, 0) / weatherChartData.length
        const avgMinTemp = weatherChartData.reduce((acc, weatherChartPoint) => acc + weatherChartPoint.MIN_TEMP, 0) / weatherChartData.length
        const avgPrecipitation = weatherChartData.reduce((acc, weatherChartPoint) => acc + weatherChartPoint.PRECIPITATION, 0) / weatherChartData.length

        context['TEMPERATURE_TITLE'] = `Avg Max (${avgMaxTemp.toFixed(2)}); Avg Min (${avgMinTemp.toFixed(2)})`
        context['PRECIPITATION_TITLE'] = `Avg (${avgPrecipitation.toFixed(2)})`
      })
    },

    createWeeklyContextData (weatherData, holidayData) {
      const contextData = {}
      const calendarDateToHolidaysMap = this.createDateToHolidaysMapWeeklyAgg(holidayData)

      weatherData.forEach(row => {
        const calendarDate = row['CALENDAR_DATE']
        const dayOfWeek = row['PART_OF_PERIOD']
        const daysToAdd = dayOfWeek - 1 >= 0 ? dayOfWeek - 1 : dayOfWeek - 1 + 7 // Sundays are mapped to 0 so we need to add a whole week
        const chartPointCalendarDate = this.$moment(calendarDate).add(daysToAdd, 'days').format('YYYY-MM-DD')
        const temperature = row['TEMPERATURE']
        const tempType = row['TEMP_TYPE'].toLowerCase()
        const precipitation = row['PRECIPITATION']

        if (!contextData[calendarDate]) {
          contextData[calendarDate] = {
            CALENDAR_DATE: calendarDate,
            WEATHER_CHART: [],
            HOLIDAYS: []
          }
        }

        const contextObj = contextData[calendarDate]
        let weatherChartDataPoint = contextObj.WEATHER_CHART.find(dataPoint => dataPoint['CALENDAR_DATE'] === chartPointCalendarDate)

        if (!weatherChartDataPoint) {
          weatherChartDataPoint = {
            CALENDAR_DATE: chartPointCalendarDate,
            MAX_TEMP: null,
            MIN_TEMP: null,
            PRECIPITATION: precipitation
          }

          contextObj.WEATHER_CHART.push(weatherChartDataPoint)
        }

        if (tempType === 'maximum') {
          weatherChartDataPoint.MAX_TEMP = temperature
        } else if (tempType === 'minimum') {
          weatherChartDataPoint.MIN_TEMP = temperature
        }
      })

      Object.keys(contextData).forEach(date => {
        const contextObj = contextData[date]
        const holidays = calendarDateToHolidaysMap[date]

        if (holidays) {
          contextObj.HOLIDAYS = this.createWeeklyContextHolidayArr(holidays)
        }
      })

      this.setWeeklyContextTitles(contextData)
      this.sortWeeklyContextWeatherChartData(contextData)

      return contextData
    },

    sortWeeklyContextWeatherChartData (contextData) {
      Object.keys(contextData).forEach(date => {
        const context = contextData[date]

        if (context.WEATHER_CHART) {
          context.WEATHER_CHART.sort((a, b) => new Date(a.CALENDAR_DATE) - new Date(b.CALENDAR_DATE))
        }
      })
    },

    createDailyContextTooltip () {
      return [
        {
          displayName: 'Temperature (\xB0C)',
          key: 'TEMPERATURE_TITLE'
        }, {
          key: 'WEATHER_CHART',
          chart: {
            xAxis: {
              tickSuffix: 'h'
            },
            dataTraces: [{
              x: 'HOUR',
              y: 'TEMPERATURE',
              color: 'red'
            }]
          }
        }, {
          displayName: 'Precipitation (mm)',
          key: 'PRECIPITATION_TITLE'
        }, {
          key: 'WEATHER_CHART',
          chart: {
            xAxis: {
              tickSuffix: 'h'
            },
            yAxis: {
              nonNegative: true
            },
            dataTraces: [{
              x: 'HOUR',
              y: 'PRECIPITATION',
              color: 'blue'
            }]
          }
        }, {
          displayName: 'Holiday(s)',
          key: 'HOLIDAYS',
          format: 'list-string',
          delimiter: ', ',
          emptyDefault: 'None'
        }
      ]
    },

    setDailyContextTitles (contextData) {
      Object.keys(contextData).forEach(key => {
        const context = contextData[key]
        let maxTemp = null
        let minTemp = null
        let maxPrecipitation = null

        context['WEATHER_CHART'].forEach((weatherPoint, index) => {
          const temperature = weatherPoint['TEMPERATURE']
          const precipitation = weatherPoint['PRECIPITATION']

          if (index === 0) {
            maxTemp = temperature
            minTemp = temperature
            maxPrecipitation = precipitation
          }

          maxTemp = Math.max(temperature, maxTemp)
          minTemp = Math.min(temperature, minTemp)
          maxPrecipitation = Math.max(precipitation, maxPrecipitation)
        })

        context['TEMPERATURE_TITLE'] = `Max (${maxTemp}); Min (${minTemp})`
        context['PRECIPITATION_TITLE'] = `Max (${maxPrecipitation})`
      })
    },

    convertDailyContextHolidaySetToArr (contextData) {
      Object.keys(contextData).forEach(date => {
        const context = contextData[date]

        context['HOLIDAYS'] = Array.from(context['HOLIDAYS'])
      })
    },

    createDateToHolidaysMapDailyAgg (holidayData) {
      return holidayData.reduce((acc, holiday) => {
        const calendarDate = holiday.CALENDAR_DATE

        if (!acc[calendarDate]) {
          acc[calendarDate] = []
        }

        acc[calendarDate].push(holiday.HOLIDAY)

        return acc
      }, {})
    },

    createDailyContextData (weatherData, holidayData) {
      const contextData = {}
      const calendarDateToHolidaysMap = this.createDateToHolidaysMapDailyAgg(holidayData)

      weatherData.forEach(row => {
        const calendarDate = row['CALENDAR_DATE']
        const temperature = row['TEMPERATURE']
        const precipitation = row['PRECIPITATION']
        const hour = row['PART_OF_PERIOD']
        const holidays = calendarDateToHolidaysMap[calendarDate]

        if (!contextData[calendarDate]) {
          contextData[calendarDate] = {
            'WEATHER_CHART': [],
            'HOLIDAYS': []
          }
        }

        contextData[calendarDate]['WEATHER_CHART'].push({
          'HOUR': hour,
          'TEMPERATURE': temperature,
          'PRECIPITATION': precipitation
        })

        if (holidays) {
          contextData[calendarDate].HOLIDAYS = holidays
        }
      })

      this.setDailyContextTitles(contextData)
      this.sortDailyContextWeatherChartData(contextData)

      return contextData
    },

    sortDailyContextWeatherChartData (contextData) {
      Object.keys(contextData).forEach(date => {
        const context = contextData[date]

        if (context.WEATHER_CHART) {
          context.WEATHER_CHART.sort((a, b) => a.HOUR - b.HOUR)
        }
      })
    },

    /**
     * Retrives the cached metric summary data from server
     */
    async getCachedMetricSummaryData () {
      try {
        return await axios.get(`${process.env.VUE_APP_API_URI}/custom-dashboards/metric-summary`)
      } catch (error) {
        console.error(`Error while getting cached metric summary data: ${error.message}`)
        this.$Message.error('Error loading metric summary!')
      }
    },

    async setValueChangeMap () {
      try {
        if (this.dashboard.widgets[0].value.length > 0) {
          this.$set(this.dashboard.widgets[0], 'valueChangeMap', {})
          const priorData = (await getData(this.priorDataQuery)).data.data
          const valueChangeMap = {}

          this.dashboard.widgets[0].value.forEach(row => {
            // TODO: Leave out latest date from value change map until
            // WoW data is available in data source
            if (row.CALENDAR_DATE !== this.latestDataDate) {
              const calendarDate = row.CALENDAR_DATE
              const priorDataObj = priorData.find(priorDataRow => priorDataRow.NEXT_CALENDAR_DATE === calendarDate)

              valueChangeMap[calendarDate] = {
                current: row,
                prior: priorDataObj
              }
            }
          })

          this.$set(this.dashboard.widgets[0], 'valueChangeMap', valueChangeMap)
        }
      } catch (error) {
        console.error(`Error while setting value change map`, error.message)
      }
    }
  }
}

const calculateColorCodeByChange = function (currentVal, previousVal) {
  const change = (currentVal - previousVal) / previousVal

  if (change > 0 && change < 0.05) {
    return '#D9EAD3'
  } else if (change >= 0.05) {
    return '#B6D8A7'
  } else if (change < 0 && change > -0.05) {
    return '#F4CDCC'
  } else if (change <= -0.05) {
    return '#EA9999'
  }

  return ''
}

const calculateColorCodeByChangeInverse = function (currentVal, previousVal) {
  const change = (currentVal - previousVal) / previousVal

  if (change < 0 && change > -0.05) {
    return '#D9EAD3'
  } else if (change <= -0.05) {
    return '#B6D8A7'
  } else if (change > 0 && change < 0.05) {
    return '#F4CDCC'
  } else if (change >= 0.05) {
    return '#EA9999'
  }

  return ''
}

const calculateColorCodeByTarget = function (value, target) {
  if (value < target) {
    return '#B6D8A7'
  } else if (value > target) {
    return '#EA9999'
  }

  return ''
}

const DASHBOARD = {
  dashboardName: 'Metrics Summary',
  title: {
    name: 'Metrics Summary',
    isShown: false
  },
  dataSource: 'LDW.REPORT.CONSOLIDATED_METRIC_SUMMARY',
  contextSource: 'production_data.dm.VIEW_MP_WEATHER_DAILY',
  isResizable: true,
  widgets: [{
    x: 0,
    y: 0,
    w: 20,
    h: 42.5,
    i: 0,
    isResizable: true,
    loading: false,
    static: false,
    metadata: {
      name: 'summaryTable',
      title: 'Summary Table',
      type: 'table',
      customField: false,
      tableType: 'horizontal',
      scroll: true
    },
    styles: {
      colMinWidth: 120,
      leftColWidth: 210
    },
    filters: [1, 2, 3],
    headerColumn: {
      name: 'CALENDAR_DATE',
      displayName: 'Calendar Date',
      format: 'date',
      fromFormat: 'YYYY-MM-DD',
      toFormat: 'MM/DD/YYYY (ddd)',
      contextTooltipDisplayName: '',
      contextTooltip: [
        // Two possible formats:
        // 1. Normal
        // {
        //   column: String,
        //   displayName: String,
        //   format: String
        // }
        // 2, Chart
      ],
      contextData: {
        // key is the header column value
        // format of value is:
        // {
        //   data: Object // Maps to value based on column value specified in contextTooltip
        //   chartData: Array<Object> // Array of objects, each object represents data point in chart
        // }
      },
      chartFormat: 'MM/DD/YY'
    },
    columns: {
      HEAD_TITLE_1: {
        displayName: 'Profitability',
        styles: {
          fontWeight: 'bold',
          textDecoration: 'underline',
          backgroundColor: 'rgb(230, 236, 240)'
        }
      },
      'City Profit % Gross': {
        displayName: 'City Profit % GB *',
        format: 'percentage',
        precision: 1,
        tooltip: [],
        styles: {
          backgroundColor: 'rgb(230, 236, 240)'
        },
        children: {
          'Contribution Margin % Gross': {
            displayName: 'Contribution Margin % GB *',
            format: 'percentage',
            precision: 1,
            tooltip: [],
            styles: {
              backgroundColor: 'rgb(230, 236, 240)'
            }
          },
          'City EBITDA % Gross': {
            displayName: 'City EBITDA % GB *',
            format: 'percentage',
            precision: 1,
            tooltip: [],
            styles: {
              backgroundColor: 'rgb(230, 236, 240)'
            }
          }
        }
      },
      'City Profit $': {
        displayName: 'City Profit *',
        format: 'currency',
        tooltip: [],
        styles: {
          backgroundColor: 'rgb(230, 236, 240)'
        },
        children: {
          'Contribution Margin $': {
            displayName: 'Contribution Margin *',
            format: 'currency',
            tooltip: [],
            styles: {
              backgroundColor: 'rgb(230, 236, 240)'
            }
          },
          'City EBITDA $': {
            displayName: 'City EBITDA *',
            format: 'currency',
            precision: 1,
            tooltip: [],
            styles: {
              backgroundColor: 'rgb(230, 236, 240)'
            }
          }
        }
      },
      HEAD_TITLE_2: {
        displayName: 'Rider',
        styles: {
          fontWeight: 'bold',
          textDecoration: 'underline',
          backgroundColor: 'rgb(245, 234, 215)'
        }
      },
      'Gross Bookings': {
        displayName: 'Gross Bookings',
        format: 'currency',
        tooltip: [],
        tooltipChangePercentage: true,
        styles: {
          backgroundColor: 'rgb(245, 234, 215)'
        },
        colorCodeFunc: calculateColorCodeByChange
      },
      Trips: {
        displayName: 'Trips',
        format: 'number',
        tooltip: [],
        tooltipChangePercentage: true,
        styles: {
          backgroundColor: 'rgb(245, 234, 215)'
        },
        colorCodeFunc: calculateColorCodeByChange
      },
      RVD: {
        displayName: 'RVD',
        format: 'currencyDecimal',
        tooltip: [],
        tooltipChangePercentage: true,
        styles: {
          backgroundColor: 'rgb(245, 234, 215)'
        },
        colorCodeFunc: calculateColorCodeByChange
      },
      RPT: {
        displayName: 'RPT',
        format: 'currencyDecimal',
        tooltip: [],
        tooltipChangePercentage: true,
        styles: {
          backgroundColor: 'rgb(245, 234, 215)'
        },
        colorCodeFunc: calculateColorCodeByChange
      },
      TVD: {
        displayName: 'TVD',
        format: 'decimal',
        tooltip: [],
        tooltipChangePercentage: true,
        styles: {
          backgroundColor: 'rgb(245, 234, 215)'
        },
        colorCodeFunc: calculateColorCodeByChange,
        children: {
          'Zero Rate': {
            displayName: 'Zero Rate',
            format: 'percentage',
            precision: 1,
            tooltip: [],
            styles: {
              backgroundColor: 'rgb(245, 234, 215)'
            }
          }
        }
      },
      'Active Riders': {
        displayName: 'Active Riders',
        format: 'number',
        tooltip: [],
        tooltipChangePercentage: true,
        styles: {
          backgroundColor: 'rgb(245, 234, 215)'
        },
        colorCodeFunc: calculateColorCodeByChange,
        children: {
          'New Accounts': {
            displayName: 'New Accounts',
            format: 'number',
            tooltip: [],
            styles: {
              backgroundColor: 'rgb(245, 234, 215)'
            }
          },
          'First Trips': {
            displayName: 'First Trips',
            format: 'number',
            tooltip: [],
            styles: {
              backgroundColor: 'rgb(245, 234, 215)'
            }
          }
        }
      },
      'Trips / Active Rider': {
        displayName: 'Trips / Active Rider',
        format: 'decimal',
        precision: 1,
        tooltip: [],
        tooltipChangePercentage: true,
        styles: {
          backgroundColor: 'rgb(245, 234, 215)'
        },
        colorCodeFunc: calculateColorCodeByChange
      },
      'Revenue / Active Rider': {
        displayName: 'Revenue / Active Rider',
        format: 'currencyDecimal',
        tooltip: [],
        tooltipChangePercentage: true,
        styles: {
          backgroundColor: 'rgb(245, 234, 215)'
        },
        colorCodeFunc: calculateColorCodeByChange
      },
      'WoW Retention': {
        displayName: 'WoW Retention',
        format: 'percentage',
        precision: 0,
        tooltip: [],
        tooltipChangePercentage: true,
        styles: {
          backgroundColor: 'rgb(245, 234, 215)'
        },
        colorCodeFunc: calculateColorCodeByChange
      },
      // 'Week 1 Cohort Retention': {
      //   displayName: 'Week 1 Cohort Retention',
      //   format: 'percentage',
      //   precision: 0,
      //   tooltip: [{
      //     column: 'PRIOR_ACTIVE_RIDERS',
      //     displayName: 'Change',
      //     format: 'percentage'
      //   }],
      //   background: {
      //     comparedColumn: 'PRIOR_ACTIVE_RIDERS',
      //     valueRange: [-0.05, 0.05],
      //     colorRange: ['F1BFC1', 'C5DABF']
      //   },
      //   styles: {
      //     backgroundColor: 'rgb(245, 234, 215)'
      //   }
      // },
      // 'Week 5 Cohort Retention': {
      //   displayName: 'Week 5 Cohort Retention',
      //   format: 'percentage',
      //   precision: 0,
      //   tooltip: [{
      //     column: 'PRIOR_ACTIVE_RIDERS',
      //     displayName: 'Change',
      //     format: 'percentage'
      //   }],
      //   background: {
      //     comparedColumn: 'PRIOR_ACTIVE_RIDERS',
      //     valueRange: [-0.05, 0.05],
      //     colorRange: ['F1BFC1', 'C5DABF']
      //   },
      //   styles: {
      //     backgroundColor: 'rgb(245, 234, 215)'
      //   }
      // },
      'Average Rating': {
        displayName: 'Average Rating',
        format: 'decimal',
        precision: 1,
        tooltip: [],
        tooltipChangePercentage: true,
        color: {
          comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
          valueRange: [0, 1],
          colorRange: ['804002', '515A6E']
        },
        styles: {
          backgroundColor: 'rgb(245, 234, 215)'
        },
        colorCodeFunc: calculateColorCodeByChange
      },
      HEAD_TITLE_3: {
        displayName: 'Fleet',
        styles: {
          fontWeight: 'bold',
          textDecoration: 'underline',
          backgroundColor: 'rgb(232, 237, 218)'
        }
      },
      'Operational Vehicle Count': {
        displayName: 'Operational Vehicle Count',
        format: 'number',
        tooltip: [],
        tooltipChangePercentage: true,
        styles: {
          backgroundColor: 'rgb(232, 237, 218)'
        },
        colorCodeFunc: calculateColorCodeByChange
        // children: {
        //   'Peak Operational Count': {
        //     displayName: 'Peak Operational Count',
        //     format: 'number',
        //     tooltip: [{
        //       column: 'PRIOR_ACTIVE_RIDERS',
        //       displayName: 'Change',
        //       format: 'percentage'
        //     }],
        //     background: {
        //       comparedColumn: 'PRIOR_ACTIVE_RIDERS',
        //       valueRange: [-0.05, 0.05],
        //       colorRange: ['F1BFC1', 'C5DABF']
        //     },
        //     styles: {
        //       backgroundColor: 'rgb(232, 237, 218)'
        //     }
        //   },
        //   'Average Operational Count': {
        //     displayName: 'Average Operational Count',
        //     format: 'number',
        //     tooltip: [{
        //       column: 'PRIOR_ACTIVE_RIDERS',
        //       displayName: 'Change',
        //       format: 'percentage'
        //     }],
        //     background: {
        //       comparedColumn: 'PRIOR_ACTIVE_RIDERS',
        //       valueRange: [-0.05, 0.05],
        //       colorRange: ['F1BFC1', 'C5DABF']
        //     },
        //     styles: {
        //       backgroundColor: 'rgb(232, 237, 218)'
        //     }
        //   }
        // }
      },
      'Ready to be Deployed Vehicle Count': {
        displayName: 'Ready to be Deployed Vehicle Count',
        format: 'number',
        tooltip: [],
        tooltipChangePercentage: true,
        styles: {
          backgroundColor: 'rgb(232, 237, 218)'
        },
        colorCodeFunc: calculateColorCodeByChangeInverse
      },
      'Backlog Vehicle Count': {
        displayName: 'Backlog Vehicle Count',
        format: 'number',
        tooltip: [],
        tooltipChangePercentage: true,
        styles: {
          backgroundColor: 'rgb(232, 237, 218)'
        },
        colorCodeFunc: calculateColorCodeByChangeInverse,
        children: {
          'IWH Backlog Vehicle Count': {
            displayName: 'IWH Backlog Vehicle Count',
            format: 'number',
            tooltip: [],
            tooltipChangePercentage: true,
            styles: {
              backgroundColor: 'rgb(232, 237, 218)'
            },
            colorCodeFunc: calculateColorCodeByChangeInverse
          },
          'OWH Backlog Vehicle Count': {
            displayName: 'OWH Backlog Vehicle Count',
            format: 'number',
            tooltip: [],
            tooltipChangePercentage: true,
            styles: {
              backgroundColor: 'rgb(232, 237, 218)'
            },
            colorCodeFunc: calculateColorCodeByChangeInverse
          }
        }
      },
      'Net Ops Decay Rate': {
        displayName: 'Net Ops Decay Rate',
        format: 'percentage',
        tooltip: [{
          displayName: 'Target Below',
          value: 0.002,
          format: 'percentage',
          precision: 2
        }],
        styles: {
          backgroundColor: 'rgb(232, 237, 218)'
        },
        colorCodeFunc: currentVal => calculateColorCodeByTarget(currentVal, 0.002),
        children: {
          'Ops Decay Rate': {
            displayName: 'Ops Decay Rate',
            format: 'percentage',
            tooltip: [],
            styles: {
              backgroundColor: 'rgb(232, 237, 218)'
            },
            children: {
              'IWH Ops Decay Rate': {
                displayName: 'IWH Ops Decay Rate',
                format: 'percentage',
                tooltip: [],
                styles: {
                  backgroundColor: 'rgb(232, 237, 218)'
                }
              },
              'OWH Ops Decay Rate': {
                displayName: 'OWH Ops Decay Rate',
                format: 'percentage',
                tooltip: [],
                styles: {
                  backgroundColor: 'rgb(232, 237, 218)'
                }
              }
            }
          },
          'Ops Revival Rate': {
            displayName: 'Ops Revival Rate',
            format: 'percentage',
            tooltip: [],
            styles: {
              backgroundColor: 'rgb(232, 237, 218)'
            },
            children: {
              'IWH Ops Revival Rate': {
                displayName: 'IWH Ops Revival Rate',
                format: 'percentage',
                tooltip: [],
                styles: {
                  backgroundColor: 'rgb(232, 237, 218)'
                }
              },
              'OWH Ops Revival Rate': {
                displayName: 'OWH Ops Revival Rate',
                format: 'percentage',
                tooltip: [],
                styles: {
                  backgroundColor: 'rgb(232, 237, 218)'
                }
              }
            }
          }
        }
      },
      // '24-hr Retrieval Rate': {
      //   displayName: '24-hr Retrival Rate',
      //   format: 'percentage',
      //   precision: 0,
      //   info: 'Sick Rate is based on PST in Local View',
      //   tooltip: [{
      //     column: 'PRIOR_SICK_RATE',
      //     displayName: 'Change',
      //     format: 'percentage'
      //   }],
      //   background: {
      //     comparedColumn: 'PRIOR_SICK_RATE',
      //     valueRange: [-0.05, 0.05],
      //     colorRange: ['C5DABF', 'F1BFC1']
      //   },
      //   styles: {
      //     backgroundColor: 'rgb(232, 237, 218)'
      //   }
      // },
      'Sick Rate': {
        displayName: 'Sick Rate',
        format: 'percentage',
        precision: 0,
        tooltip: [{
          displayName: 'Target Below',
          value: 0.08,
          format: 'percentage',
          precision: 2
        }],
        styles: {
          backgroundColor: 'rgb(232, 237, 218)'
        },
        colorCodeFunc: currentVal => calculateColorCodeByTarget(currentVal, 0.08)
      },
      'Hold rate (Hold as % of total vehicle)': {
        displayName: 'Hold Rate',
        format: 'percentage',
        precision: 0,
        info: 'Hold as % of Total Vehicle',
        tooltip: [],
        tooltipChangePercentage: true,
        styles: {
          backgroundColor: 'rgb(232, 237, 218)'
        },
        colorCodeFunc: calculateColorCodeByChangeInverse
      },
      HEAD_TITLE_4: {
        displayName: 'Labor',
        styles: {
          fontWeight: 'bold',
          textDecoration: 'underline',
          backgroundColor: 'rgb(243, 235, 243)'
        }
      },
      'Labor Cost % Gross': {
        displayName: 'Labor Cost % GB * (Finance)',
        format: 'percentage',
        precision: 1,
        tooltip: [{
          displayName: 'Target Below',
          value: 0.21,
          format: 'percentage',
          precision: 2
        }],
        styles: {
          backgroundColor: 'rgb(243, 235, 243)'
        },
        colorCodeFunc: currentVal => calculateColorCodeByTarget(currentVal, 0.21),
        children: {
          'In Warehouse Cost % Gross': {
            displayName: 'IWH Cost % GB (HB)',
            format: 'percentage',
            precision: 1,
            tooltip: [],
            styles: {
              backgroundColor: 'rgb(243, 235, 243)'
            },
            children: {
              'Daily Mechanic Count': {
                displayName: 'Daily Mechanic Count',
                format: 'number',
                tooltip: [],
                styles: {
                  backgroundColor: 'rgb(243, 235, 243)'
                }
              },
              'Mech / 100 Vehicles Ratio': {
                displayName: 'Mech / 100 Vehicles Ratio',
                format: 'number',
                precision: 1,
                tooltip: [],
                styles: {
                  backgroundColor: 'rgb(243, 235, 243)'
                }
              }
            }
          },
          'Out of Warehouse Cost % Gross': {
            displayName: 'OWH Cost % GB (HB)',
            format: 'percentage',
            precision: 1,
            tooltip: [],
            styles: {
              backgroundColor: 'rgb(243, 235, 243)'
            },
            children: {
              'Daily OS Count': {
                displayName: 'Daily OS Count',
                format: 'number',
                tooltip: [],
                styles: {
                  backgroundColor: 'rgb(243, 235, 243)'
                }
              },
              'OS / 100 Vehicles Ratio': {
                displayName: 'OS / 100 Vehicles Ratio',
                format: 'number',
                precision: 1,
                tooltip: [],
                styles: {
                  backgroundColor: 'rgb(243, 235, 243)'
                }
              }
            }
          },
          // 'Mech Cost % Gross': {
          //   displayName: 'Mech Cost % GB',
          //   format: 'percentage',
          //   precision: 1,
          //   tooltip: [{
          //     column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
          //     displayName: 'Local Ops Payout Submitted',
          //     format: 'percentage',
          //     precision: 1
          //   }],
          //   styles: {
          //     backgroundColor: 'rgb(243, 235, 243)'
          //   },
          //   children: {
          //     'Daily Mechanic Count': {
          //       displayName: 'Daily Mechanic Count',
          //       format: 'number',
          //       precision: 1,
          //       tooltip: [{
          //         column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
          //         displayName: 'Local Ops Payout Submitted',
          //         format: 'percentage',
          //         precision: 1
          //       }],
          //       styles: {
          //         backgroundColor: 'rgb(243, 235, 243)'
          //       }
          //     },
          //     'Mech / 100 Vehicles Ratio': {
          //       displayName: 'Mech / 100 Vehicles Ratio',
          //       format: 'number',
          //       precision: 1,
          //       tooltip: [{
          //         column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
          //         displayName: 'Local Ops Payout Submitted',
          //         format: 'percentage',
          //         precision: 1
          //       }],
          //       styles: {
          //         backgroundColor: 'rgb(243, 235, 243)'
          //       }
          //     }
          //   }
          // },
          // 'OS Cost % Gross': {
          //   displayName: 'OS Cost % GB',
          //   format: 'percentage',
          //   precision: 1,
          //   tooltip: [{
          //     column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
          //     displayName: 'Local Ops Payout Submitted',
          //     format: 'percentage',
          //     precision: 1
          //   }],
          //   styles: {
          //     backgroundColor: 'rgb(243, 235, 243)'
          //   },
          //   children: {
          //     DAILY_OS_COUNT: {
          //       displayName: 'Daily OS Count',
          //       format: 'number',
          //       precision: 1,
          //       tooltip: [{
          //         column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
          //         displayName: 'Local Ops Payout Submitted',
          //         format: 'percentage',
          //         precision: 1
          //       }],
          //       color: {
          //         comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
          //         valueRange: [0, 1],
          //         colorRange: ['804002', '515A6E']
          //       },
          //       styles: {
          //         backgroundColor: 'rgb(243, 235, 243)'
          //       }
          //     },
          //     OS_100_VEHICLES_RATIO: {
          //       displayName: 'OS / 100 Vehicles Ratio',
          //       format: 'number',
          //       precision: 1,
          //       tooltip: [{
          //         column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
          //         displayName: 'Local Ops Payout Submitted',
          //         format: 'percentage',
          //         precision: 1
          //       }],
          //       color: {
          //         comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
          //         valueRange: [0, 1],
          //         colorRange: ['804002', '515A6E']
          //       },
          //       styles: {
          //         backgroundColor: 'rgb(243, 235, 243)'
          //       }
          //     }
          //   }
          // },
          'Juicer Cost % Gross': {
            displayName: 'Juicer Cost % GB',
            format: 'percentage',
            precision: 1,
            tooltip: [],
            color: {
              comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
              valueRange: [0, 1],
              colorRange: ['804002', '515A6E']
            },
            styles: {
              backgroundColor: 'rgb(243, 235, 243)'
            },
            children: {
              'Juicer Payout % Gross': {
                displayName: 'Juicer Payout % GB',
                format: 'percentage',
                precision: 1,
                tooltip: [],
                color: {
                  comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
                  valueRange: [0, 1],
                  colorRange: ['804002', '515A6E']
                },
                styles: {
                  backgroundColor: 'rgb(243, 235, 243)'
                }
              },
              'Juicer Payment Fees % Gross': {
                displayName: 'Juicer Payment Fees % GB',
                format: 'percentage',
                precision: 1,
                tooltip: [],
                color: {
                  comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
                  valueRange: [0, 1],
                  colorRange: ['804002', '515A6E']
                },
                styles: {
                  backgroundColor: 'rgb(243, 235, 243)'
                }
              }
            }
          },
          // 'Homebase Labor / Finance Labor': {
          //   displayName: 'Homebase Labor / Finance Labor',
          //   format: 'percentage',
          //   precision: 0,
          //   tooltip: [{
          //     column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
          //     displayName: 'Local Ops Payout Submitted',
          //     format: 'percentage',
          //     precision: 1
          //   }],
          //   color: {
          //     comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
          //     valueRange: [0, 1],
          //     colorRange: ['804002', '515A6E']
          //   },
          //   styles: {
          //     backgroundColor: 'rgb(243, 235, 243)'
          //   }
          // },
          'Labor Cost Submission %': {
            displayName: 'Labor Cost Submission %',
            format: 'percentage',
            precision: 0,
            tooltip: [],
            color: {
              comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
              valueRange: [0, 1],
              colorRange: ['804002', '515A6E']
            },
            styles: {
              backgroundColor: 'rgb(243, 235, 243)'
            }
          }
        }
      },
      'Total Tasks Created': {
        displayName: 'Total Tasks Created',
        format: 'number',
        tooltip: [],
        tooltipChangePercentage: true,
        styles: {
          backgroundColor: 'rgb(243, 235, 243)'
        },
        colorCodeFunc: calculateColorCodeByChangeInverse,
        children: {
          'Manual Tasks Created': {
            displayName: 'Manual Tasks Created',
            format: 'number',
            tooltip: [],
            tooltipChangePercentage: true,
            styles: {
              backgroundColor: 'rgb(243, 235, 243)'
            },
            colorCodeFunc: calculateColorCodeByChangeInverse
          },
          'Automated Tasks Created': {
            displayName: 'Automated Tasks Created',
            format: 'number',
            tooltip: [],
            tooltipChangePercentage: true,
            styles: {
              backgroundColor: 'rgb(243, 235, 243)'
            },
            colorCodeFunc: calculateColorCodeByChangeInverse
          }
        }
      },
      'Total Tasks Accomplished': {
        displayName: 'Total Tasks Accomplished',
        format: 'number',
        tooltip: [],
        tooltipChangePercentage: true,
        styles: {
          backgroundColor: 'rgb(243, 235, 243)'
        },
        colorCodeFunc: calculateColorCodeByChange
      },
      HEAD_TITLE_5: {
        displayName: 'Other',
        styles: {
          fontWeight: 'bold',
          textDecoration: 'underline',
          backgroundColor: 'rgb(223, 231, 232)'
        }
      },
      'Local Ops Other % Gross': {
        displayName: 'Local Ops Other % GB',
        format: 'percentage',
        precision: 1,
        tooltip: [],
        styles: {
          backgroundColor: 'rgb(223, 231, 232)'
        },
        children: {
          'Spare Parts % Gross': {
            displayName: 'Spare Parts % GB *',
            format: 'percentage',
            precision: 1,
            tooltip: [],
            color: {
              comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
              valueRange: [0, 1],
              colorRange: ['804002', '515A6E']
            },
            styles: {
              backgroundColor: 'rgb(223, 231, 232)'
            }
          },
          'Van & Fuel Costs % Gross': {
            displayName: 'Van & Fuel Costs % GB *',
            format: 'percentage',
            precision: 1,
            tooltip: [],
            color: {
              comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
              valueRange: [0, 1],
              colorRange: ['804002', '515A6E']
            },
            styles: {
              backgroundColor: 'rgb(223, 231, 232)'
            }
          },
          'Fines & Permits % Gross': {
            displayName: 'Fines & Permits % GB *',
            format: 'percentage',
            precision: 1,
            tooltip: [],
            color: {
              comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
              valueRange: [0, 1],
              colorRange: ['804002', '515A6E']
            },
            styles: {
              backgroundColor: 'rgb(223, 231, 232)'
            }
          },
          'Other % Gross': {
            displayName: 'Other % GB',
            format: 'percentage',
            precision: 1,
            tooltip: [],
            styles: {
              backgroundColor: 'rgb(223, 231, 232)'
            }
          }
        }
      }
      // 'Local Ops Other % Gross': {
      //   displayName: 'Local Ops Other % GB *',
      //   format: 'percentage',
      //   precision: 1,
      //   tooltip: [{
      //     column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      //     displayName: 'Local Ops Payout Submitted',
      //     format: 'percentage',
      //     precision: 1
      //   }],
      //   color: {
      //     comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      //     valueRange: [0, 1],
      //     colorRange: ['804002', '515A6E']
      //   },
      //   styles: {
      //     backgroundColor: 'rgb(223, 231, 232)'
      //   },
      //   children: {
      //     'Spare Parts % Gross': {
      //       displayName: 'Spare Parts % GB *',
      //       format: 'percentage',
      //       precision: 1,
      //       tooltip: [{
      //         column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      //         displayName: 'Local Ops Payout Submitted',
      //         format: 'percentage',
      //         precision: 1
      //       }],
      //       color: {
      //         comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      //         valueRange: [0, 1],
      //         colorRange: ['804002', '515A6E']
      //       },
      //       styles: {
      //         backgroundColor: 'rgb(223, 231, 232)'
      //       }
      //     },
      //     'Van & Fuel Costs % Gross': {
      //       displayName: 'Van & Fuel Costs % GB *',
      //       format: 'percentage',
      //       precision: 1,
      //       tooltip: [{
      //         column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      //         displayName: 'Local Ops Payout Submitted',
      //         format: 'percentage',
      //         precision: 1
      //       }],
      //       color: {
      //         comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      //         valueRange: [0, 1],
      //         colorRange: ['804002', '515A6E']
      //       },
      //       styles: {
      //         backgroundColor: 'rgb(223, 231, 232)'
      //       }
      //     },
      //     'Fines & Permits % Gross': {
      //       displayName: 'Fines & Permits % GB *',
      //       format: 'percentage',
      //       precision: 1,
      //       tooltip: [{
      //         column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      //         displayName: 'Local Ops Payout Submitted',
      //         format: 'percentage',
      //         precision: 1
      //       }],
      //       color: {
      //         comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      //         valueRange: [0, 1],
      //         colorRange: ['804002', '515A6E']
      //       },
      //       styles: {
      //         backgroundColor: 'rgb(223, 231, 232)'
      //       }
      //     }
      //   }
      // },
      // 'CSR Costs % Gross': {
      //   displayName: 'CX Costs % GB',
      //   format: 'percentage',
      //   precision: 1,
      //   tooltip: [{
      //     column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      //     displayName: 'Local Ops Payout Submitted',
      //     format: 'percentage',
      //     precision: 1
      //   }],
      //   color: {
      //     comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      //     valueRange: [0, 1],
      //     colorRange: ['804002', '515A6E']
      //   },
      //   styles: {
      //     backgroundColor: 'rgb(223, 231, 232)'
      //   },
      //   children: {
      //     'CSR Generated Tickets': {
      //       displayName: 'CX Generated Tickets',
      //       format: 'number',
      //       precision: 1,
      //       tooltip: [{
      //         column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      //         displayName: 'Local Ops Payout Submitted',
      //         format: 'percentage',
      //         precision: 1
      //       }],
      //       color: {
      //         comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      //         valueRange: [0, 1],
      //         colorRange: ['804002', '515A6E']
      //       },
      //       styles: {
      //         backgroundColor: 'rgb(223, 231, 232)'
      //       }
      //     },
      //     'Average Rating': {
      //       displayName: 'Average Rating',
      //       format: 'decimal',
      //       precision: 1,
      //       tooltip: [{
      //         column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      //         displayName: 'Local Ops Payout Submitted',
      //         format: 'percentage',
      //         precision: 1
      //       }],
      //       color: {
      //         comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      //         valueRange: [0, 1],
      //         colorRange: ['804002', '515A6E']
      //       },
      //       styles: {
      //         backgroundColor: 'rgb(223, 231, 232)'
      //       }
      //     },
      //     'CSR Rate % Trips': {
      //       displayName: 'CX Rate % Trips',
      //       format: 'percentage',
      //       tooltip: [{
      //         column: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      //         displayName: 'Local Ops Payout Submitted',
      //         format: 'percentage',
      //         precision: 1
      //       }],
      //       color: {
      //         comparedColumn: 'LOCAL_OPS_PAYOUT_SUBMITTED',
      //         valueRange: [0, 1],
      //         colorRange: ['804002', '515A6E']
      //       },
      //       styles: {
      //         backgroundColor: 'rgb(223, 231, 232)'
      //       }
      //     }
      //   }
      // }
    },
    chart: {
      type: 'line',
      width: 150,
      xAxis: 'CALENDAR_DATE'
    },
    value: [],
    valueChangeMap: {}
  }, {
    x: 20,
    y: 0,
    w: 4,
    h: 4,
    i: 1,
    metadata: {
      name: 'PRODUCT',
      title: 'Product',
      type: 'select',
      customField: false
    },
    query: 'SELECT DISTINCT PRODUCT FROM LDW.REPORT.CONSOLIDATED_METRIC_SUMMARY ORDER BY PRODUCT ASC',
    value: 'All Products',
    options: []
  }, {
    x: 20,
    y: 4,
    w: 4,
    h: 4,
    i: 2,
    metadata: {
      name: 'DISPLAY_NAME',
      title: 'Region',
      type: 'select',
      customField: false
    },
    query: 'SELECT DISTINCT DISPLAY_NAME FROM LDW.REPORT.CONSOLIDATED_METRIC_SUMMARY ORDER BY DISPLAY_NAME ASC',
    value: 'All Regions - All Regions',
    options: []
  }, {
    x: 20,
    y: 8,
    w: 4,
    h: 4,
    i: 3,
    metadata: {
      name: 'DATE_AGG',
      title: 'Period',
      type: 'select',
      customField: false
    },
    query: 'SELECT DISTINCT DATE_AGG FROM LDW.REPORT.CONSOLIDATED_METRIC_SUMMARY ORDER BY DATE_AGG ASC',
    value: 'Weekly',
    options: []
  }, {
    x: 20,
    y: 12,
    w: 4,
    h: 3,
    i: 4,
    metadata: {
      name: 'NUMBER_FILTER',
      title: '# of Periods',
      type: 'input-number',
      customField: true
    },
    value: 8
  }, {
    x: 20,
    y: 20,
    w: 4,
    h: 2,
    i: 5,
    metadata: {
      type: 'blank',
      customField: true
    }
  }, {
    x: 20,
    y: 22,
    w: 4,
    h: 1,
    i: 6,
    metadata: {
      type: 'text',
      customField: true
    },
    value: '<b>Last Updated:</b> N/A'
  }, {
    x: 20,
    y: 23,
    w: 4,
    h: 2,
    i: 7,
    metadata: {
      type: 'blank',
      customField: true
    }
  }, {
    x: 20,
    y: 25,
    w: 4,
    h: 2,
    i: 8,
    metadata: {
      type: 'text',
      customField: true
    },
    value: 'For <b>Metrics Summary Definitions</b>, refer to this <a href="https://docs.google.com/document/d/18hybkrJFpQ0w9n2lmjuXq1Y2mkf0RpcV9XB7r1GYsqs/edit?usp=sharing" target="_blank"><b>document</b></a>'
  }, {
    x: 20,
    y: 27,
    w: 4,
    h: 2,
    i: 9,
    metadata: {
      type: 'blank',
      customField: true
    }
  }, {
    x: 20,
    y: 29,
    w: 4,
    h: 2,
    i: 10,
    metadata: {
      type: 'report',
      customField: true
    }
  }, {
    x: 20,
    y: 31,
    w: 4,
    h: 2,
    i: 11,
    metadata: {
      type: 'blank',
      customField: true
    }
  }, {
    x: 20,
    y: 33,
    w: 4,
    h: 2,
    i: 12,
    metadata: {
      type: 'text',
      customField: true
    },
    styles: {
      fontWeight: 'bold',
      backgroundColor: 'rgb(239, 235, 232)'
    },
    value: '* Indicates Finance Estimation is used to calculate metric'
  }, {
    x: 20,
    y: 35,
    w: 4,
    h: 2,
    i: 13,
    metadata: {
      type: 'blank',
      customField: true
    }
  }, {
    x: 20,
    y: 37,
    w: 4,
    h: 4,
    i: 14,
    metadata: {
      type: 'text',
      customField: true
    },
    value: 'Hover over calendar dates to view contextual (weather/holiday) data. Only works for <b>"Region" geo aggregations</b> and <b>"Weekly/Daily" periods</b>.'
  }]
}
</script>
